/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable jsx-a11y/media-has-caption */
import { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import cn from "classnames";
import styles from "components/shared/stays/Video.module.scss";

/**
 * @param {boolean} autoPlay - Automatically start playback
 * @param {boolean} controls - Show native controls if true
 * @param {boolean} loop - Loop playback if true
 * @param {boolean} muted - Video is muted if true
 * @param {boolean} playsInline - Video will play inline if true
 * @param {string} posterSrc - Url for poster image
 * @param {string} videoSrc - Url for video
 * @param {string} posterLqipSrc - Url for low quality placeholder image
 * @param {node} className - Class to be applied to the component
 * @param {string} objectFit - Define how the video and placeholder occupy the space allowed
 * **** - [cover] - Fills the entire space, cropping content to match aspect ratio
 * **** - [contain] - Scale the video so it fits within the space provided
 * @param {string} layout - Define how the video is positioned within it's containing element
 * **** - [fill] - Video is absolutely positioned and will take the maximum space provided
 * **** - [responsive] - Video is relatively positioned and will take the maximum space provided
 */

export default function Video({
  autoPlay,
  controls,
  loop,
  muted,
  playsInline,
  posterSrc,
  videoSrc,
  posterLqipSrc,
  layout,
  objectFit = "",
  className = null,
}) {
  const [isLoaded, setIsLoaded] = useState(false);
  const videoEl = useRef(null);

  const vidProps = {
    controls,
    autoPlay,
    loop,
    muted,
    playsInline,
  };

  if (posterSrc) {
    vidProps.poster = posterSrc;
  }

  function handleLoaded() {
    setIsLoaded(true);
  }

  useEffect(() => {
    const video = videoEl.current;
    video.addEventListener("loadeddata", handleLoaded());
    return function cleanup() {
      video.addEventListener("loadeddata", handleLoaded());
    };
  }, []);

  return (
    videoSrc && (
      <div
        className={cn(
          styles.component,
          layout === "fill" && styles.fill,
          layout === "responsive" && styles.responsive,
          objectFit === "contain" && styles.contain,
          objectFit === "cover" && styles.cover,
          className
        )}
      >
        {posterLqipSrc && !isLoaded && (
          <div className={styles.placeholder}>
            <img alt="Video placeholder" src={posterLqipSrc} />
          </div>
        )}
        <video className={styles.video} {...vidProps} ref={videoEl}>
          <source src={videoSrc} type="video/mp4" />
        </video>
      </div>
    )
  );
}

Video.propTypes = {
  videoSrc: PropTypes.string.isRequired,
  posterSrc: PropTypes.string,
  posterLqipSrc: PropTypes.string,
  layout: PropTypes.string,
  objectFit: PropTypes.string,
  className: PropTypes.node,
  autoPlay: PropTypes.bool,
  controls: PropTypes.bool,
  loop: PropTypes.bool,
  muted: PropTypes.bool,
  playsInline: PropTypes.bool,
};
